import { optional } from '@/helpers/dto2resource';
import { RequiredKeys } from '@/helpers/types';
import PropostaNovoDto from '../dto/PropostaNovoDto';
import MeioPagamento from '../enums/MeioPagamento';
import { IPromessa } from '../IPromessa';
import BoletoResource from './BoletoResource';
import DetalhesPropostaResource from './DetalhesPropostaResource';
import DividaPropostaResource from './DividaPropostaResource';
import OfertaResource from './OfertaResource';
import ParcelasGerenciadasResource from './ParcelasGerenciadasResource';

export default class PropostaNovoResource implements IPromessa {
  private readonly desconto: {
    valor: number | undefined;
    percentual: number | undefined
  };

  id: number;
  status: number;
  dataProposta: Date;
  divida: DividaPropostaResource;
  parcelas: BoletoResource[];

  parcelasGerenciadas: ParcelasGerenciadasResource;
  possuiTermoAcordo?: boolean;
  urlTermoAcordo?: string;
  valorTotal: number;
  detalheDescricao?: string;
  detalhe?: DetalhesPropostaResource;
  valorEntrada: number;
  quantidadeParcelas: number;
  valorParcela: number;
  dataPrimeiroBoleto: Date;
  possuiEntrada: boolean;
  meioPagamentoEscolhido: MeioPagamento;
  meiosPagamentoPrimeiraParcela: MeioPagamento[];
  meiosPagamentoDemaisParcelas: MeioPagamento[];
  negociacaoParcial?: boolean;
  oferta?: OfertaResource;
  isAcordoExterno: boolean;
  permiteCancelamento: boolean;

  constructor(dto: PropostaNovoDto) {
    this.id = dto.id;
    this.status = dto.status;
    this.dataProposta = new Date(dto.dataProposta);
    this.divida = new DividaPropostaResource(dto.divida);
    this.parcelas = dto.parcelas
      .map((p) => new BoletoResource(p))
      .sort((a, b) => a.numeroParcela - b.numeroParcela);

    this.parcelasGerenciadas = new ParcelasGerenciadasResource(dto.parcelasGerenciadas);
    this.possuiTermoAcordo = optional(dto.possuiTermoAcordo);
    this.urlTermoAcordo = dto.urlTermoAcordo ?? undefined;
    this.valorTotal = dto.valorTotal;
    this.detalhe = dto.detalhe ? new DetalhesPropostaResource(dto.detalhe) : undefined;
    this.detalheDescricao = this.detalhe?.descricao;
    this.valorEntrada = dto.valorEntrada;
    this.quantidadeParcelas = dto.quantidadeParcelas;
    this.valorParcela = dto.valorParcela;
    this.dataPrimeiroBoleto = new Date(dto.dataPrimeiroBoleto);
    this.possuiEntrada = dto.possuiEntrada;
    this.meiosPagamentoPrimeiraParcela = dto.meiosPagamentoPrimeiraParcela
      ?? [MeioPagamento.Boleto];
    this.meiosPagamentoDemaisParcelas = dto.meiosPagamentoDemaisParcelas ?? [];
    this.meioPagamentoEscolhido = dto.meioPagamentoEscolhido;

    this.desconto = {
      valor: dto.valorDesconto,
      percentual: dto.percentualDesconto,
    };
    this.negociacaoParcial = dto.negociacaoParcial;
    this.oferta = dto.oferta ? new OfertaResource(dto.oferta) : undefined;

    this.isAcordoExterno = false;

    this.permiteCancelamento = !!dto.permiteCancelamento;
  }

  get valorDesconto(): number {
    if (!this.divida.valorTotal) return 0;
    return this.desconto.valor
      ?? this.oferta?.detalhe?.valores?.valorDesconto
      ?? this.divida.valorTotal - this.valorTotal;
  }

  get percentualDesconto(): number {
    if (!this.divida.valorTotal) return 0;

    return this.desconto.percentual
      ?? this.oferta?.detalhe?.valores?.percentualDesconto
      ?? Math.trunc((this.valorDesconto * 100) / this.divida.valorTotal);
  }

  get valorTotalCalculado(): number {
    return this.valorEntrada + this.quantidadeParcelas * this.valorParcela;
  }

  get proximaParcelaPendente(): BoletoResource | undefined {
    const parcelasPendentesComData = this.parcelas
      ?.filter((p): p is RequiredKeys<BoletoResource, 'dataVencimento'> => (
        !!p.dataVencimento && !p.pago
      ));

    if (!parcelasPendentesComData?.length) return undefined;

    return parcelasPendentesComData
      .reduce((prv, cur) => (prv.dataVencimento > cur.dataVencimento ? cur : prv));
  }

  get dataQuitacao() {
    if (!this.quantidadeParcelasMaisEntrada) return undefined;

    const ultimaParcela = this
      .parcelas?.find((p) => p.numeroParcela === this.quantidadeParcelasMaisEntrada);
    return ultimaParcela?.dataPagamento;
  }

  get quantidadeParcelasMaisEntrada(): number {
    return this.quantidadeParcelas + (this.possuiEntrada ? 1 : 0);
  }

  get possuiParcela(): boolean {
    return !!this.parcelas
      ?.some((parcela) => (
        parcela.boletoEmitido
        || parcela.pixEmitido
        || parcela.pago
        || parcela.quitada
      ));
  }

  get reconhecePagamentoImediato() {
    if (this.meioPagamentoEscolhido === MeioPagamento.Pix) {
      return this.divida.parametros?.pixReconhecimentoImediato === true;
    }

    return false;
  }
}
